import { useState, useCallback, useEffect } from 'react';
import { socialMediaApi } from '../api';
import type {
  SocialPlatform,
  SocialAnalyticsRange,
  PlatformAnalyticsStat,
  AudienceTimePoint,
  SocialEngagementByDay,
  PostEngagementRow,
} from '@/lib/api';

// ── Re-exported domain types (consumed by AnalyticsPage and chart components) ─

export type { SocialAnalyticsRange as DateRange };
export type { PlatformAnalyticsStat as PlatformStat };
export type { AudienceTimePoint as FollowerTimePoint };
export type { SocialEngagementByDay as EngagementByDay };
export type { PostEngagementRow as PostAnalyticsRow };

export interface FollowerDistribution {
  platform: SocialPlatform;
  count: number;
}

export interface SocialAnalyticsData {
  platforms: PlatformAnalyticsStat[];
  followerTimeSeries: AudienceTimePoint[];
  engagementByDay: SocialEngagementByDay[];
  followerDistribution: FollowerDistribution[];
  postAnalytics: PostEngagementRow[];
  postsHeatmap: Array<{ date: string; count: number }>;
  lastRefreshedAt: Date;
}

export interface UseSocialAnalyticsReturn extends SocialAnalyticsData {
  isLoading: boolean;
  error: string | null;
  dateRange: SocialAnalyticsRange;
  setDateRange: (range: SocialAnalyticsRange) => void;
  refresh: () => void;
}

// ── Placeholder data (used while loading or on first render) ──────────────────

function buildEmptyData(): SocialAnalyticsData {
  return {
    platforms: [],
    followerTimeSeries: [],
    engagementByDay: [],
    followerDistribution: [],
    postAnalytics: [],
    postsHeatmap: [],
    lastRefreshedAt: new Date(),
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useSocialAnalytics(): UseSocialAnalyticsReturn {
  const [dateRange, setDateRange] = useState<SocialAnalyticsRange>('30d');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SocialAnalyticsData | null>(null);

  const fetchAnalytics = useCallback(async (range: SocialAnalyticsRange) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await socialMediaApi.getSocialMediaAnalytics({ range });

      // Build followerDistribution from the latest audience snapshot per platform
      const followerDistribution: FollowerDistribution[] = result.platforms
        .filter((p) => p.audienceSize !== null)
        .map((p) => ({ platform: p.platform, count: p.audienceSize! }));

      setData({
        platforms: result.platforms,
        followerTimeSeries: result.audienceTimeSeries,
        engagementByDay: result.engagementByDay,
        followerDistribution,
        postAnalytics: result.postEngagement,
        postsHeatmap: result.postsHeatmap,
        lastRefreshedAt: new Date(result.generatedAt),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAnalytics(dateRange);
  }, [fetchAnalytics, dateRange]);

  const refresh = useCallback(() => {
    void fetchAnalytics(dateRange);
  }, [fetchAnalytics, dateRange]);

  const empty = buildEmptyData();

  return {
    isLoading,
    error,
    dateRange,
    setDateRange,
    refresh,
    platforms: data?.platforms ?? empty.platforms,
    followerTimeSeries: data?.followerTimeSeries ?? empty.followerTimeSeries,
    engagementByDay: data?.engagementByDay ?? empty.engagementByDay,
    followerDistribution: data?.followerDistribution ?? empty.followerDistribution,
    postAnalytics: data?.postAnalytics ?? empty.postAnalytics,
    postsHeatmap: data?.postsHeatmap ?? empty.postsHeatmap,
    lastRefreshedAt: data?.lastRefreshedAt ?? empty.lastRefreshedAt,
  };
}
